import React from 'react';

/**
 * WizardShell — the step-sidebar + content layout shared by every multi-step guided flow in the
 * app (ReadinessWizard, PestleWizard, AiUseCaseWizard, SubmitReportPage), previously
 * reimplemented from scratch in each one. Only the layout/step-rail is shared here; each
 * wizard's own step content and Next/Submit branching logic stay exactly where they are - a
 * wizard is otherwise too specific to its own data model to usefully generalize further.
 */
export interface WizardStep {
  id: string;
  label: string;
  icon?: string;
  /** 0-100. Drives the mini progress bar on this step's nav row. */
  progressPct: number;
  done?: boolean;
  /** Optional secondary text next to the progress bar, e.g. "3/8 answered". */
  meta?: React.ReactNode;
}

interface WizardShellProps {
  steps: WizardStep[];
  currentStepIndex: number;
  onStepSelect: (index: number) => void;
  sidebarTitle?: string;
  sidebarIcon?: string;
  children: React.ReactNode;
}

export const WizardShell: React.FC<WizardShellProps> = ({
  steps,
  currentStepIndex,
  onStepSelect,
  sidebarTitle = 'Steps',
  sidebarIcon = 'ri-list-unordered',
  children,
}) => (
  <div className="np-detail-layout">
    <div className="np-meta-rail">
      <div className="np-card">
        <div className="np-card-head">
          <i className={`${sidebarIcon} text-primary`}></i>
          <h2>{sidebarTitle}</h2>
        </div>
        <div className="np-nav-list">
          {steps.map((step, idx) => {
            const isActive = idx === currentStepIndex;
            return (
              <button
                key={step.id}
                type="button"
                className={`np-nav-item ${isActive ? 'active' : ''} ${step.done ? 'done' : ''}`}
                onClick={() => onStepSelect(idx)}
              >
                {step.icon && <i className={`${step.icon} np-nav-item-icon`}></i>}
                <div className="np-nav-item-body">
                  <div className="np-nav-item-title">{step.label}</div>
                  <div className="np-nav-item-meta">
                    <div className="np-progress" style={{ height: 3, flex: 1 }}>
                      <div className={`np-fill ${step.done ? 'np-tone-success' : ''}`} style={{ width: `${step.progressPct}%` }} />
                    </div>
                    {step.meta && <span className="np-nav-item-count np-tabular">{step.meta}</span>}
                  </div>
                </div>
                {step.done && <i className={`ri-check-line ${isActive ? '' : 'text-success'}`}></i>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
    <div>{children}</div>
  </div>
);

/**
 * WizardStepFooter — the Previous/[right-side action] footer bar every wizard ends its current
 * step with. The right side is a slot rather than a fixed Next/Submit prop because that
 * branching (Next vs. Save & Next vs. Submit-with-remaining-count) is different enough per
 * wizard that forcing one shape would just move the special-casing here instead of removing it.
 */
export const WizardStepFooter: React.FC<{
  onBack: () => void;
  backDisabled?: boolean;
  children: React.ReactNode;
}> = ({ onBack, backDisabled, children }) => (
  <div className="d-flex justify-content-between align-items-center mt-2 pt-3 border-top">
    <button type="button" className="np-btn np-btn-ghost" onClick={onBack} disabled={backDisabled}>
      <i className="ri-arrow-left-line"></i>Previous
    </button>
    <div className="d-flex align-items-center gap-2">{children}</div>
  </div>
);
